import {
  ColumnConfig,
  RowConfig,
  CellState,
  CellType,
  CellConfigValue,
} from '@/shared/types/table';
import {
  TableCell,
  PrimaryCell,
  StatusCell,
  ActionsCell,
  LinkCell,
  AvatarsCell,
  TextFieldCell,
  DropdownCell,
  ButtonCell,
  ProgressBarCell,
  SkeletonCell,
  TagsCell,
  CheckboxCell,
  IconCell,
  ImagePreviewCell,
  StarsCell,
  DateCell,
  GanttCell,
} from '../atoms';

interface CellRendererProps<T = unknown> {
  column: ColumnConfig<T>;
  row: RowConfig<T>;
  value: unknown;
  /** Valor original del accessor (sin pasar por render) - usado para edición */
  rawValue?: unknown;
  selected?: boolean;
  onCellFocus?: (rowId: string | number, columnId: string) => void;
  isCellFocused?: (rowId: string | number, columnId: string) => boolean;
  onCellChange?: (rowId: string | number, columnId: string, value: unknown) => void;
  /** Posición left para columnas sticky (en píxeles) */
  stickyLeft?: number;
  /** Posición right para columnas de acciones sticky (en píxeles) */
  stickyRight?: number;
  /** Es la última columna bloqueada (para mostrar borde separador) */
  isLastLocked?: boolean;
  /** Si es una columna de acciones */
  isActionsColumn?: boolean;
  hasColumnMaxWidthAuto?: boolean;
}

export const CellRenderer = <T,>({
  column,
  row,
  value,
  rawValue,
  selected = false,
  onCellFocus,
  isCellFocused,
  onCellChange,
  stickyLeft = 0,
  stickyRight,
  isLastLocked = false,
  isActionsColumn = false,
  hasColumnMaxWidthAuto = false,
}: CellRendererProps<T>) => {
  const isFocused = isCellFocused?.(row.id, column.id) || false;
  // Verificar si la fila está en modo edición
  const isRowEditing = row._isEditing === true;
  // Solo aplicar estado 'selected' si está seleccionado (NO cuando está en modo edición)
  // El modo edición solo aplica fondo y bordes, no el color de texto azul
  const cellState: CellState = isFocused ? 'focused' : selected ? 'selected' : 'default';

  // Detectar si value es un objeto con configuración de celda editable (modo legacy)
  const isEditableConfig =
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'type' in value &&
    'value' in value;
  const actualValue = isEditableConfig ? (value as CellConfigValue).value : value;
  const cellOnChange = isEditableConfig ? (value as CellConfigValue).onChange : undefined;

  // Determinar el tipo de celda basado en la configuración de editable
  let cellType: CellType | undefined = column.type;
  let cellColumn: ColumnConfig<T> = column;
  let isEditMode = false;

  if (isEditableConfig) {
    // Modo legacy: usar la configuración del value
    const valueConfig = value as CellConfigValue;
    cellType = valueConfig.type as CellType | undefined;
    cellColumn = { ...column, columnOptions: valueConfig.options } as ColumnConfig<T>;
    isEditMode = true;
  } else if (column.editable && column.editable !== 'none' && isRowEditing) {
    // Modo automático: solo activar edición si la fila está en modo edición
    cellType = column.editable;
    cellColumn = { ...column, columnOptions: column.columnOptions } as ColumnConfig<T>;
    isEditMode = true;
  }

  // Handler unificado que combina onChange personalizado y el handler global
  const handleChange = (newValue: unknown) => {
    // Primero ejecutar el onChange personalizado si existe (para compatibilidad)
    cellOnChange?.(newValue);
    // Luego ejecutar el handler global para notificar al componente Table
    onCellChange?.(row.id, column.id, newValue);
  };

  // Determinar qué valor usar:
  // - En modo edición: usar rawValue (valor original del accessor) si está disponible
  // - En modo vista: usar actualValue (resultado del render)
  const valueForCell = isEditMode && rawValue !== undefined ? rawValue : actualValue;

  const cellProps = {
    value: valueForCell,
    row: row as RowConfig<unknown>,
    column: cellColumn as ColumnConfig<unknown>,
    className: column.cellClassName,
    state: cellState,
    onCellFocus: () => onCellFocus?.(row.id, column.id),
    onChange: handleChange,
    stickyLeft,
    stickyRight,
    isLastLocked,
    isActionsColumn,
    isRowEditing,
    hasColumnMaxWidthAuto,
  };

  switch (cellType) {
    case 'primary':
      return <PrimaryCell {...cellProps} />;
    case 'status':
      return <StatusCell {...cellProps} />;
    case 'actions':
      return <ActionsCell {...cellProps} />;
    case 'link':
      return <LinkCell {...cellProps} />;
    case 'avatars':
      return <AvatarsCell {...cellProps} />;
    case 'textfield':
      return <TextFieldCell {...cellProps} />;
    case 'dropdown':
      return <DropdownCell {...cellProps} />;
    case 'button':
      return <ButtonCell {...cellProps} />;
    case 'progressbar':
      return <ProgressBarCell {...cellProps} />;
    case 'skeleton':
      return <SkeletonCell {...cellProps} />;
    case 'tags':
      return <TagsCell {...cellProps} />;
    case 'checkbox':
      return <CheckboxCell {...cellProps} />;
    case 'icon':
      return <IconCell {...cellProps} />;
    case 'imagePreview':
      return <ImagePreviewCell {...cellProps} />;
    case 'stars':
      return <StarsCell {...cellProps} />;
    case 'date':
      return <DateCell {...cellProps} />;
    case 'gantt':
      return <GanttCell {...cellProps} rawValue={rawValue} />;
    default:
      return <TableCell {...cellProps} rawValue={rawValue} />;
  }
};
